import React, { useState, useEffect } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useParams } from "react-router-dom";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
} from "react-bootstrap";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "" }],
    ["link", "image"],
    [
      { align: "" },
      { align: "center" },
      { align: "right" },
      { align: "justify" },
    ],
    [{ "line-height": [] }],
    ["clean"],
  ],
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "align",
];

function EditBlogs() {
  const { id } = useParams();
  const [blogTitle, setBlogTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [category, setCategory] = useState("");
  const [author, setAuthor] = useState({
    name: "",
    email: "",
    description: "",
    profilePic: "",
  });
  const [comments, setComments] = useState([]);
  const [responseMessage, setResponseMessage] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [thumbnail, setthumbnail] = useState("");
  const [tags, setTags] = useState("");

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        const [blogResponse, commentsResponse] = await Promise.all([
          axios.get(`https://blog-backend-ivfv.onrender.com/editblogs/${id}`),
          axios.get(`https://blog-backend-ivfv.onrender.com/blogs/${id}/comments`),
        ]);

        const blogData = blogResponse.data;
        setBlogTitle(blogData.title);
        setSummary(blogData.summary);
        setContent(blogData.content);
        setthumbnail(blogData.thumbnail)
        setTags(blogData.tags);
        setImage(blogData.image);
        setCategory(blogData.category);
        setAuthor(blogData.author || {
          name: "",
          email: "",
          description: "",
          profilePic: "",
        });
        setComments(commentsResponse.data);
      } catch (error) {
        console.error("Error fetching blog data:", error);
        setResponseMessage("Error fetching blog data.");
      }
    };

    fetchBlogData();
  }, [id]);

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setthumbnail(file);
    }
  };

  const handleAuthorChange = (e) => {
    const { name, value } = e.target;
    setAuthor((prevAuthor) => ({
      ...prevAuthor,
      [name]: value,
    }));
  };

  const updateBlog = async () => {
    try {
      const formData = new FormData();
      formData.append("title", blogTitle);
      formData.append("summary", summary);
      formData.append("content", content);
      formData.append("category", category);
      const tagsArray = typeof tags === "string"
        ? tags.split(",").map((tag) => tag.trim())
        : Array.isArray(tags)
          ? tags
          : [];

      formData.append("tags", JSON.stringify(tagsArray));
      formData.append("author", JSON.stringify(author));
      if (image) formData.append("image", image);
      if (thumbnail && typeof thumbnail !== "string") {
        formData.append("thumbnail", thumbnail); // New file upload
      } else {
        formData.append("existingThumbnail", thumbnail); // Send previous image path as separate field
      } const response = await axios.put(
        `https://blog-backend-ivfv.onrender.com/superadmineditblogs/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        setResponseMessage("Blog updated successfully!");
        toast.success("Blog updated successfully");
      } else {
        setResponseMessage("Error updating blog.");
      }
    } catch (error) {
      console.error("Error updating blog:", error);
      setResponseMessage("Error updating blog.");
    }
  };

  const deleteComment = async (commentId) => {
    try {
      const response = await axios.delete(
        `https://blog-backend-ivfv.onrender.com/comments/${commentId}`
      );
      if (response.status === 200) {
        setComments(comments.filter((comment) => comment._id !== commentId));
        toast.error("Comment deleted successfully");
      } else {
        toast.error("Error deleting comment.");
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Error deleting comment.");
    }
  };

  return (
    <Container className="my-4">
      <ToastContainer />
      <Card className="p-4">
        <h3 className="mb-4">Edit Blog</h3>

        <Form.Group controlId="formThumbnail" className="mb-3">
          <Form.Label>Edit Thumbnail</Form.Label>

          {thumbnail && typeof thumbnail === "string" && (
            <div className="mb-2">
              <img
                src={`https://blog-backend-ivfv.onrender.com/${thumbnail}`}
                alt="Current Thumbnail"
                style={{ maxWidth: "200px", borderRadius: "8px" }}
              />
            </div>
          )}

          {/* File input for new thumbnail */}
          <Form.Control type="file" onChange={handleThumbnailChange} />
        </Form.Group>


        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            value={blogTitle}
            onChange={(e) => setBlogTitle(e.target.value)}
            placeholder="Enter blog title"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Summary</Form.Label>
          <Form.Control
            type="text"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Enter summary"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Tags (comma-separated)</Form.Label>
          <Form.Control
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g. tech, react, blog"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Content</Form.Label>
          <ReactQuill
            value={content}
            onChange={(value) => setContent(value)}
            modules={modules}
            formats={formats}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Category</Form.Label>
          <Form.Control
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Enter or update category"
          />
        </Form.Group>

        <h5 className="mt-4">Author Details</h5>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={author.name}
                onChange={handleAuthorChange}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={author.email}
                onChange={handleAuthorChange}
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            name="description"
            rows={3}
            value={author.description}
            onChange={handleAuthorChange}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Profile Pic URL</Form.Label>
          <Form.Control
            type="text"
            name="profilePic"
            value={author.profilePic}
            onChange={handleAuthorChange}
          />
        </Form.Group>

        <div className="d-flex gap-3 mt-3">
          <Button variant="primary" onClick={updateBlog}>
            Update Blog
          </Button>
          <Button variant="secondary" onClick={() => setShowPreview(!showPreview)}>
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
        </div>

        {responseMessage && <p className="mt-3 text-info">{responseMessage}</p>}
      </Card>

      {showPreview && (
        <Card className="p-4 mt-4">
          <h4>Preview</h4>
          <h5>{blogTitle}</h5>
          <p>{summary}</p>
          {image && <img src={`data:image/jpeg;base64,${image}`} alt="Preview" width="200" />}
          <div dangerouslySetInnerHTML={{ __html: content }} />
        </Card>
      )}

      <Card className="p-4 mt-4">
        <h4>Comments</h4>
        {comments.length === 0 ? (
          <p>No comments on the blog yet.</p>
        ) : (
          comments.map((comment) => (
            <Card key={comment._id} className="my-2 p-2">
              <p>{comment.text}</p>
              <small>Posted by: {comment.user}</small><br />
              <small>{moment(comment.createdAt).fromNow()}</small><br />
              <Button
                variant="danger"
                size="sm"
                className="mt-2"
                onClick={() => deleteComment(comment._id)}
              >
                Delete
              </Button>
            </Card>
          ))
        )}
      </Card>
    </Container>
  );
}

export default EditBlogs;
